import { ReactNode, useEffect, useId, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { BinSmallTrashIcon } from "../../assets/icons/binsmall";
import { CheckedIcon } from "../../assets/icons/checkedIcon";
import { CloseIcon } from "../../assets/icons/closeIcon";
import { DropdownIcon } from "../../assets/icons/dropdownIcon";
import { FrameIcon } from "../../assets/icons/frameIcon";
import { designSystemVars as vars } from "../tokens";
import { ButtonBase, IconButton } from "./Button";
import { FieldLabel, FormControl, Input, SelectMenu } from "./Form";
import {
  PersonListOption,
  PersonOptionAvatar,
  PersonOptionName,
  PersonOptionRow,
} from "./PersonList";

export const DEFAULT_PERSON_AVATAR =
  "https://cdn-icons-png.freepik.com/512/3135/3135789.png";

export interface PersonPickerItem {
  id: string;
  name: string;
  image?: string;
}

interface PersonPickerProps<T extends PersonPickerItem> {
  label: string;
  selected: T[];
  onToggle: (person: T) => void;
  onRemove: (id: string) => void;
  renderMenu: (search: string, onToggle: (person: T) => void) => ReactNode;
  searchPlaceholder?: string;
  width?: string;
  variant?: "default" | "field";
  onOpen?: () => void;
}

export function PersonPicker<T extends PersonPickerItem>({
  label,
  selected,
  onToggle,
  onRemove,
  renderMenu,
  searchPlaceholder = "Search Asqio Database",
  width = "514px",
  variant = "default",
  onOpen,
}: PersonPickerProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mouseDownRef = useRef(false);
  const suppressFocusOpenRef = useRef(false);
  const labelId = useId();
  const menuId = useId();

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (open && event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
        suppressFocusOpenRef.current =
          document.activeElement !== inputRef.current;
        inputRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [open]);

  const openMenu = () => {
    if (!open) onOpen?.();
    setOpen(true);
  };
  const togglePerson = (person: T) => {
    onToggle(person);
    setSearch("");
    inputRef.current?.focus();
  };

  return (
    <Picker
      ref={wrapperRef}
      $open={open}
      $focusVisible={keyboardFocus}
      $width={width}
      $field={variant === "field"}
      onMouseDown={() => {
        mouseDownRef.current = true;
        setKeyboardFocus(false);
      }}
      onClickCapture={(event) => {
        // Internal actions can blur the input before restoring focus on click.
        if (event.detail > 0) mouseDownRef.current = true;
      }}
      onClick={() => {
        mouseDownRef.current = false;
      }}
    >
      <PickerLabel id={labelId}>{label}</PickerLabel>
      <PickerContent
        $field={variant === "field"}
        onClick={() => inputRef.current?.focus()}
      >
        {selected.map((person) => (
          <PickerTag key={person.id}>
            <TagAvatar src={person.image || DEFAULT_PERSON_AVATAR} alt="" />
            <TagName>{person.name}</TagName>
            <RemoveTagButton
              variant="ghost"
              aria-label={`Remove ${person.name}`}
              onClick={(event) => {
                event.stopPropagation();
                onRemove(person.id);
                inputRef.current?.focus();
              }}
            >
              <BinSmallTrashIcon />
            </RemoveTagButton>
          </PickerTag>
        ))}
        <PickerInput
          $field={variant === "field"}
          ref={inputRef}
          role="combobox"
          aria-labelledby={labelId}
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-autocomplete="list"
          value={search}
          placeholder={selected.length === 0 ? searchPlaceholder : "Search"}
          onFocus={() => {
            if (!suppressFocusOpenRef.current) openMenu();
            suppressFocusOpenRef.current = false;
            setKeyboardFocus(!mouseDownRef.current);
            mouseDownRef.current = false;
          }}
          onBlur={() => {
            mouseDownRef.current = false;
            setKeyboardFocus(false);
          }}
          onChange={(event) => {
            setSearch(event.target.value);
            openMenu();
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              if (!open) openMenu();
              else
                wrapperRef.current
                  ?.querySelector<HTMLElement>('[role="option"][tabindex="0"]')
                  ?.focus();
            }
          }}
        />
      </PickerContent>
      <ToggleButton
        $field={variant === "field"}
        type="button"
        aria-label={`${open ? "Close" : "Open"} ${label} menu`}
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          if (open) setOpen(false);
          else {
            if (document.activeElement === inputRef.current) openMenu();
            else inputRef.current?.focus();
          }
          mouseDownRef.current = false;
        }}
      >
        {open ? <CloseIcon /> : <DropdownIcon />}
      </ToggleButton>
      {open && (
        <PickerMenu
          id={menuId}
          role="listbox"
          aria-label={label}
          aria-multiselectable="true"
          onKeyDown={(event) => {
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key))
              return;
            const options = Array.from(
              event.currentTarget.querySelectorAll<HTMLElement>(
                '[role="option"][tabindex="0"]',
              ),
            );
            if (!options.length) return;
            event.preventDefault();
            const currentIndex = options.indexOf(
              document.activeElement as HTMLElement,
            );
            const nextIndex =
              event.key === "Home"
                ? 0
                : event.key === "End"
                  ? options.length - 1
                  : (currentIndex +
                      (event.key === "ArrowDown" ? 1 : -1) +
                      options.length) %
                    options.length;
            options[nextIndex].focus();
          }}
        >
          {renderMenu(search, togglePerson)}
        </PickerMenu>
      )}
    </Picker>
  );
}

export function PersonPickerOption({
  person,
  selected,
  disabled = false,
  label = person.name,
  onToggle,
}: {
  person: PersonPickerItem;
  selected: boolean;
  disabled?: boolean;
  label?: string;
  onToggle: () => void;
}) {
  return (
    <PersonListOption
      $disabled={disabled}
      role="option"
      aria-disabled={disabled}
      aria-selected={selected}
      aria-label={label}
      tabIndex={disabled ? -1 : 0}
      onClick={() => {
        if (!disabled) onToggle();
      }}
      onKeyDown={(event) => {
        if (!disabled && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <PersonOptionRow>
        {selected ? <CheckedIcon /> : <FrameIcon />}
        <PersonOptionAvatar
          src={person.image || DEFAULT_PERSON_AVATAR}
          alt={person.name}
        />
        <PersonOptionName>{label}</PersonOptionName>
      </PersonOptionRow>
    </PersonListOption>
  );
}

const Picker = styled(FormControl)<{
  $open: boolean;
  $width: string;
  $field: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: ${({ $width }) => $width};
  max-width: 100%;
  min-height: ${({ $field }) =>
    $field
      ? `calc(${vars.spacing.px64} + ${vars.borderWidth.thin})`
      : vars.spacing.px48};
  justify-content: ${({ $field }) => ($field ? "center" : "flex-start")};
  gap: ${vars.spacing.none};
  padding: ${({ $field }) => ($field ? vars.spacing.none : vars.spacing.xs)}
    ${vars.spacing.md};
  box-sizing: border-box;
  z-index: ${({ $open }) => ($open ? vars.zIndex.dropdown : "auto")};
  ${({ $open }) =>
    $open &&
    css`
      border: ${vars.borderWidth.medium} solid ${vars.color.border.accent};
      border-bottom: 0;
      border-radius: ${vars.radius.sm} ${vars.radius.sm} ${vars.radius.none}
        ${vars.radius.none};
    `}
`;
const PickerLabel = styled(FieldLabel).attrs({ as: "div" })`
  height: ${vars.spacing.xl};
`;
const PickerContent = styled.div<{ $field: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  min-height: ${({ $field }) =>
    $field ? vars.spacing.xl : vars.spacing["3xl"]};
  gap: ${vars.spacing.xs};
  padding-right: calc(${vars.spacing.xl} + ${vars.spacing.xs});
  box-sizing: border-box;
  cursor: text;
`;
const PickerInput = styled(Input)<{ $field: boolean }>`
  flex: 1 1 ${vars.spacing.px120};
  min-width: ${vars.spacing.px100};
  height: ${({ $field }) => ($field ? vars.spacing.xl : vars.spacing["3xl"])};
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: ${vars.color.background.transparent};
`;
const ToggleButton = styled(ButtonBase)<{ $field: boolean }>`
  position: absolute;
  top: ${({ $field }) =>
    $field
      ? `calc((${vars.spacing.px64} + ${vars.borderWidth.thin} - ${vars.spacing.xl}) / 2 - ${vars.borderWidth.thin})`
      : `calc(${vars.spacing.xs} + ${vars.spacing.xl})`};
  right: ${vars.spacing.md};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${vars.spacing.xl};
  height: ${({ $field }) => ($field ? vars.spacing.xl : vars.spacing["3xl"])};
  margin-left: auto;
  padding: 0;
  border: none;
  background: ${vars.color.background.transparent};
`;
const PickerMenu = styled(SelectMenu)`
  top: 100%;
  left: calc(-1 * ${vars.borderWidth.medium});
  width: calc(100% + 2 * ${vars.borderWidth.medium});
  padding: ${vars.spacing.none};
  overflow: hidden;
`;
const PickerTag = styled.div`
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 220px;
  height: ${vars.spacing["3xl"]};
  padding: ${vars.spacing.xs} ${vars.spacing.sm};
  gap: ${vars.spacing.xs};
  box-sizing: border-box;
  border-radius: ${vars.radius.sm};
  border: ${vars.borderWidth.thin} solid
    ${vars.color.dataVisualization.nine.border};
  background: ${vars.color.dataVisualization.nine.background};
`;
const RemoveTagButton = styled(IconButton)`
  width: ${vars.spacing.xl};
  min-width: ${vars.spacing.xl};
  height: ${vars.spacing.xl};
`;
const TagAvatar = styled(PersonOptionAvatar)`
  width: ${vars.spacing.xl};
  height: ${vars.spacing.xl};
  border-radius: ${vars.radius.circle};
  flex-shrink: 0;
  padding: 0;
`;
const TagName = styled.div`
  min-width: 0;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${vars.color.dataVisualization.nine.text};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  line-height: ${vars.typography.lineHeight.sm};
`;
